import { useEffect, useRef, useState } from "react";
import { Search, Star, Package, ExternalLink, Trophy, Truck } from "lucide-react";
import { api, apiError } from "../api/client";
import LoginGate from "../components/LoginGate";
import { EmptyState, ErrorState, inr } from "../components/States";
import { MaskedLine } from "../components/Motion";
import { Seo } from "../components/Seo";

export const ProductImage = ({ src, alt }) => {
    const [err, setErr] = useState(false);
    if (!src || err)
        return (
            <div className="flex aspect-square w-full items-center justify-center rounded-xl bg-cream">
                <Package className="h-10 w-10 text-smoke/40" />
            </div>
        );
    return (
        <img
            src={src}
            alt={alt}
            loading="lazy"
            onError={() => setErr(true)}
            className="aspect-square w-full rounded-xl bg-cream object-contain p-3"
        />
    );
};

export const ProductCard = ({ p, isBest }) => {
    const name = p.productName || p.name || p.title || "Product";
    const store = p.platform || p.store || "";
    const url = p.deeplink || p.url || p.link;
    const delivery = p.delivery || p.deliveryTime || p.eta;
    const mrp = p.mrp ?? p.mrpPrice;
    const discount = p.discount ?? p.discountPercent ?? p.discountPct;
    return (
        <div
            data-testid="product-card"
            className="group relative flex flex-col rounded-2xl border border-ink/5 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
        >
            {isBest && (
                <span className="absolute left-4 top-4 z-10 inline-flex items-center gap-1 rounded-full bg-leaf px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
                    <Trophy className="h-3 w-3" /> Lowest
                </span>
            )}
            {store && (
                <span className="absolute right-4 top-4 z-10 rounded-full bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-cream">
                    {store}
                </span>
            )}
            <ProductImage src={p.image} alt={name} />
            <h3 className="mt-4 line-clamp-2 font-display text-sm font-medium leading-snug tracking-tight text-ink">
                {name}
            </h3>
            <p className="mt-1 text-xs text-smoke">
                {[p.brand, p.quantity].filter(Boolean).join(" · ")}
            </p>
            <div className="mt-3 flex flex-wrap items-baseline gap-2">
                <span className="font-display text-xl font-semibold tracking-tight text-ink" data-testid="product-price">
                    {inr(p.price)}
                </span>
                {mrp != null && p.price != null && mrp > p.price && (
                    <span className="text-sm text-smoke line-through">{inr(mrp)}</span>
                )}
                {discount != null && discount > 0 && (
                    <span className="rounded-full bg-saffron-soft px-2 py-0.5 text-[11px] font-bold text-saffron">
                        {discount}% off
                    </span>
                )}
            </div>
            {p.shippingKnown && p.shippingCost > 0 && p.totalPrice != null && (
                <p className="mt-1 text-[11px] text-smoke" data-testid="product-total">
                    + {inr(p.shippingCost)} delivery · total {inr(p.totalPrice)}
                </p>
            )}
            {p.shippingKnown && p.shippingCost === 0 && (
                <p className="mt-1 text-[11px] font-medium text-leaf" data-testid="product-free-delivery">
                    Free delivery
                </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-smoke">
                {p.rating != null && (
                    <span className="inline-flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 fill-saffron text-saffron" />
                        {p.rating}
                    </span>
                )}
                {delivery && (
                    <span className="inline-flex items-center gap-1">
                        <Truck className="h-3.5 w-3.5" />
                        {delivery}
                    </span>
                )}
                {p.availability != null && (
                    <span className={p.availability ? "text-leaf" : "text-red-600"}>
                        {p.availability ? "In stock" : "Unavailable"}
                    </span>
                )}
            </div>
            {url && (
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="product-deeplink"
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-india px-5 py-2.5 text-sm font-medium text-india transition-colors hover:bg-india hover:text-white"
                >
                    View on {store || "store"}
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            )}
        </div>
    );
};

const STATUS_LABELS = {
    identifying: "Identifying the exact product…",
    searching: "Searching live stores…",
    collecting: "Collecting offers…",
    matching: "Matching the same variant across stores…",
    comparing: "Comparing total prices…",
};

const Comparator = () => {
    const [query, setQuery] = useState("");
    const [job, setJob] = useState(null);
    const [error, setError] = useState("");
    const pollRef = useRef(null);

    const stopPolling = () => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
    };
    useEffect(() => stopPolling, []);

    const running = job && job.status !== "done" && job.status !== "failed";

    const poll = (jobId, attempt = 0) => {
        stopPolling();
        if (attempt > 40) {
            setError("This comparison is taking longer than usual. Please try again.");
            return;
        }
        pollRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get(`/products/compare-live/${jobId}`);
                setJob(data);
                if (data.status !== "done" && data.status !== "failed") poll(jobId, attempt + 1);
            } catch (e) {
                setError(apiError(e));
            }
        }, 2500);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        stopPolling();
        setError("");
        setJob(null);
        try {
            const { data } = await api.post("/products/compare-live", { query: query.trim() });
            setJob(data);
            if (data.status !== "done" && data.status !== "failed") poll(data.jobId);
        } catch (err) {
            setError(apiError(err));
        }
    };

    const offers = job?.offers || [];
    const total = (o) => o.totalPrice ?? o.price ?? Infinity;
    const sorted = [...offers].sort((a, b) => total(a) - total(b));
    const bestTotal = sorted.length ? total(sorted[0]) : null;

    const storeChips = Object.entries(
        offers.reduce((acc, o) => {
            const k = o.store;
            if (!k) return acc;
            acc[k] = acc[k] || { count: 0, min: Infinity };
            acc[k].count += 1;
            if (total(o) < acc[k].min) acc[k].min = total(o);
            return acc;
        }, {})
    );

    return (
        <div className="mx-auto max-w-7xl px-6 py-12 sm:px-8 lg:px-12" data-testid="price-comparison-tool">
            <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row" data-testid="compare-form">
                <div className="flex flex-1 items-center gap-2 rounded-full border border-ink/10 bg-white p-2 transition-colors focus-within:border-saffron">
                    <Search className="ml-3 h-5 w-5 shrink-0 text-saffron" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="compare-query-input"
                        placeholder="Search a product — e.g. iPhone 16, boAt earbuds"
                        aria-label="Product to compare"
                        className="w-full bg-transparent text-base text-ink placeholder:text-smoke/60 focus:outline-none"
                    />
                </div>
                <button
                    type="submit"
                    disabled={running}
                    data-testid="compare-submit-button"
                    className="shrink-0 rounded-full bg-saffron px-8 py-4 text-sm font-medium text-white transition-colors hover:bg-saffron-hover disabled:opacity-60"
                >
                    {running ? "Comparing…" : "Compare prices"}
                </button>
            </form>

            {running && (
                <div className="mt-12" data-testid="compare-loading">
                    <div className="flex items-center gap-3 rounded-2xl border border-ink/5 bg-white p-6 shadow-sm">
                        <div className="h-6 w-6 shrink-0 animate-spin rounded-full border-2 border-ink/10 border-t-saffron" />
                        <div>
                            <p className="font-medium text-ink" data-testid="compare-status">
                                {STATUS_LABELS[job?.status] || "Comparing live store prices…"}
                            </p>
                            <p className="mt-0.5 text-xs text-smoke">Checking multiple stores for the exact same product — usually under 30 seconds.</p>
                        </div>
                    </div>
                    <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="animate-pulse rounded-2xl border border-ink/5 bg-white p-5">
                                <div className="aspect-square w-full rounded-xl bg-ink/5" />
                                <div className="mt-4 h-4 w-full rounded bg-ink/5" />
                                <div className="mt-2 h-4 w-1/2 rounded bg-ink/5" />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {error && <ErrorState message={error} onRetry={() => query.trim() && submit({ preventDefault() {} })} />}

            {job && !running && (
                <div className="mt-10">
                    <div className="flex flex-wrap items-center gap-3" data-testid="compare-summary">
                        <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">
                            {job.identity?.productName
                                ? `${job.identity.productName}${job.identity.brand ? ` — ${job.identity.brand}` : ""}`
                                : `“${job.query}”`}
                        </h2>
                        <span className="rounded-full bg-india/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-india">
                            {sorted.length} listing{sorted.length === 1 ? "" : "s"}
                        </span>
                        {job.identity?.category && (
                            <span className="rounded-full bg-ink/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-smoke">
                                {job.identity.category}
                            </span>
                        )}
                    </div>

                    {sorted.length > 0 ? (
                        <>
                            {storeChips.length > 0 && (
                                <div className="mt-5 flex flex-wrap gap-2" data-testid="compare-platforms">
                                    {storeChips.map(([name, meta]) => (
                                        <span
                                            key={name}
                                            data-testid={`platform-chip-${name.toLowerCase().replace(/\s+/g, "-")}`}
                                            className="rounded-full border border-ink/10 bg-white px-4 py-2 text-xs font-medium text-ink"
                                        >
                                            {name} · {meta.count}{meta.min !== Infinity ? ` · from ${inr(meta.min)}` : ""}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="compare-results">
                                {sorted.map((p, i) => (
                                    <ProductCard key={p.id || `${p.store}-${i}`} p={p} isBest={bestTotal != null && total(p) === bestTotal && sorted.length > 1} />
                                ))}
                            </div>
                            {(job.sources?.length > 0 || job.otherVariants?.length > 0 || job.mismatches?.length > 0) && (
                                <div className="mt-8 space-y-3">
                                    {job.otherVariants?.length > 0 && (
                                        <details className="rounded-2xl border border-ink/5 bg-white p-5" data-testid="compare-other-variants">
                                            <summary className="cursor-pointer text-sm font-medium text-ink">
                                                Other variants found ({job.otherVariants.length}) — different sizes, packs or models
                                            </summary>
                                            <ul className="mt-3 space-y-1.5">
                                                {job.otherVariants.slice(0, 8).map((v, i) => (
                                                    <li key={i} className="text-xs text-smoke">
                                                        {v.title || v.name || "Variant"}
                                                        {v.store ? ` · ${v.store}` : ""}
                                                        {v.price != null ? ` · ${inr(v.price)}` : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        </details>
                                    )}
                                    <p className="text-xs leading-relaxed text-smoke" data-testid="compare-sources">
                                        {job.sources?.length > 0 &&
                                            `Sources: ${job.sources.map((s) => `${s.name.replace(/_/g, " ")} (${s.count ?? 0} results)`).join(" · ")}. `}
                                        {job.mismatches?.length > 0 &&
                                            `${job.mismatches.length} look-alike listing${job.mismatches.length === 1 ? "" : "s"} with different variants were filtered out. `}
                                        Prices include delivery where the store reports it. Prices and availability can change — confirm on the store page before buying.
                                    </p>
                                </div>
                            )}
                        </>
                    ) : (
                        <EmptyState
                            title={job.message || "No live prices found"}
                            message="Try a more specific product name — include the brand and model for the most accurate comparison."
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const PriceComparisonPage = () => (
    <>
        <Seo
            title="Price Comparison — SahiKhoj"
            description="Compare live product prices across supported shopping and quick-commerce platforms on SahiKhoj."
            path="/price-comparison"
        />
        <section className="hero-glow bg-ink pb-14 pt-36 text-cream sm:pb-16 lg:pt-44">
            <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                    <span className="h-px w-10 bg-saffron" />
                    Price Comparison
                </p>
                <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl">
                    <MaskedLine delay={0.15}>
                        Same product. <span className="text-saffron">Every store's price.</span>
                    </MaskedLine>
                </h1>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-mist">
                    Compare live prices across supported shopping and quick-commerce
                    stores before you buy.
                </p>
            </div>
        </section>
        <section className="bg-cream pb-24">
            <LoginGate feature="Price comparison">
                <Comparator />
            </LoginGate>
        </section>
    </>
);

export default PriceComparisonPage;
